// API configuration - Single source of truth for API URL
// Priority: Environment variable > Default URL
const DEFAULT_API_URL = 'http://localhost:3000'  // Default backend API URL
const IS_DEV = process.env.NODE_ENV !== 'production'

// In development, use the backend URL directly for reliability
// In production, use the environment variable or default URL
export const API_BASE_URL = IS_DEV ? (process.env.VUE_APP_API_URL || DEFAULT_API_URL) : (process.env.VUE_APP_API_URL || DEFAULT_API_URL)

// Debug logging
console.log('API Configuration:')
console.log('process.env.VUE_APP_API_URL:', process.env.VUE_APP_API_URL)
console.log('DEFAULT_API_URL:', DEFAULT_API_URL)
console.log('Final API_BASE_URL:', API_BASE_URL)

export const API_ENDPOINTS = {
  docs: '/api/docs',
  auth: {
    login: '/api/auth/login',
    register: '/api/auth/register',
    me: '/api/auth/me',
    createUserDirect: '/api/auth/create-user-direct' // New admin-level user creation
  },
  llm: {
    models: '/api/llms/models',
    create: '/api/llms/models',
    get: (id) => `/api/llms/models/${id}`,
    update: (id) => `/api/llms/models/${id}`,
    delete: (id) => `/api/llms/models/${id}`,
    options: '/api/llms/models/options',
    chat: '/api/llms/chat'
  },
  agents: {
    list: '/api/agents',
    create: '/api/agents',
    get: (id) => `/api/agents/${id}`,
    update: (id) => `/api/agents/${id}`,
    delete: (id) => `/api/agents/${id}`,
    chat: (agentId) => `/api/agents/${agentId}/chat`,
    // New conversation endpoints
    conversations: (agentId) => `/api/agents/${agentId}/conversations`,
    conversationsAll: (agentId) => `/api/agents/${agentId}/conversations/all`,
    conversationsDiagnose: (agentId) => `/api/agents/${agentId}/conversations/diagnose`,
    sessions: (agentId) => `/api/agents/${agentId}/sessions`,
    session: (sessionId) => `/api/agents/sessions/${sessionId}`,
    deleteSession: (sessionId) => `/api/agents/sessions/${sessionId}`,
    // Legacy endpoints (for backwards compatibility)
    chatHistory: (agentId) => `/api/agents/${agentId}/chat/history`,
    chatSessions: (agentId) => `/api/agents/${agentId}/chat/sessions`,
    sessionMessages: (agentId, sessionId) => `/api/agents/${agentId}/chat/sessions/${sessionId}/messages`,
    availableKB: (agentId) => `/api/agents/${agentId}/available-kb`
  },
  orgs: {
    list: '/api/orgs',
    create: '/api/orgs',
    get: (id) => `/api/orgs/${id}`,
    update: (id) => `/api/orgs/${id}`,
    delete: (id) => `/api/orgs/${id}`,
    members: (id) => `/api/orgs/${id}/members`,
    addMember: (id) => `/api/orgs/${id}/members`,
    updateMemberRole: (id, userId) => `/api/orgs/${id}/members/${userId}/role`,
    removeMember: (id, userId) => `/api/orgs/${id}/members/${userId}`
  },
  knowledgeBases: {
    list: '/api/knowledge-bases',
    create: '/api/knowledge-bases',
    get: (id) => `/api/knowledge-bases/${id}`,
    update: (id) => `/api/knowledge-bases/${id}`,
    delete: (id) => `/api/knowledge-bases/${id}`,
    documents: (id) => `/api/knowledge-bases/${id}/documents`,
    search: (id) => `/api/knowledge-bases/${id}/search`
  },
  documents: {
    upload: (kbId) => `/api/knowledge-bases/${kbId}/documents`,
    get: (id) => `/api/documents/${id}`,
    update: (id) => `/api/documents/${id}`,
    delete: (id) => `/api/documents/${id}`,
    download: (id) => `/api/documents/${id}/download`
  },
  memory: {
    upload: '/api/memory/upload',
    files: '/api/memory/files',
    agentFiles: (id) => `/api/memory/agent/files/${id}`,
    file: (id) => `/api/memory/files/${id}`,
    download: (id) => `/api/memory/files/${id}/download`,
    delete: (id) => `/api/memory/files/${id}`
  },
  messaging: {
    initialize: '/api/messaging/initialize',
    getConfig: (platform) => `/api/messaging/${platform}/config`,
    getConfigByInstance: (platform, instanceId) => `/api/messaging/${platform}/${instanceId}/config`,
    updateConfig: '/api/messaging/update/config',
    deleteConfig: (platform) => `/api/messaging/${platform}/config`,
    deleteConfigByInstance: (platform, instanceId) => `/api/messaging/${platform}/${instanceId}/config`,
    sendMessage: '/api/messaging/send',
    webhook: (instanceId, platform) => `/api/messaging/${instanceId}/${platform}/webhook`,
    // New token management endpoints
    setToken: '/api/messaging/tokens/set',
    getTokenRequirements: '/api/messaging/tokens/requirements',
    getStatus: '/api/messaging/status',
    getMissingTokens: (platform) => `/api/messaging/${platform}/missing-tokens`,
    platforms: '/api/messaging/platforms'
  },
  tools: {
    catalog: '/api/tools/catalog',
    test: '/api/tools/test'
  },
  workstreams: {
    list: '/api/workstreams',
    create: '/api/workstreams',
    get: (id) => `/api/workstreams/${id}`,
    update: (id) => `/api/workstreams/${id}`,
    delete: (id) => `/api/workstreams/${id}`,
    stats: '/api/workstreams/stats',
    members: (id) => `/api/workstreams/${id}/members`,
    addMember: (id) => `/api/workstreams/${id}/members`,
    removeMember: (id, memberID) => `/api/workstreams/${id}/members/${memberID}`,
    updateMemberRole: (id, memberID) => `/api/workstreams/${id}/members/${memberID}/role`,
    // Functions
    functions: (id) => `/api/workstreams/${id}/functions`,
    createFunction: (id) => `/api/workstreams/${id}/functions`,
    updateFunction: (id, functionId) => `/api/workstreams/${id}/functions/${functionId}`,
    deleteFunction: (id, functionId) => `/api/workstreams/${id}/functions/${functionId}`,
    // Goals under a workstream
    goals: (id) => `/api/workstreams/${id}/goals`,
    createGoal: (id) => `/api/workstreams/${id}/goals`,
    updateGoal: (id, goalId) => `/api/workstreams/${id}/goals/${goalId}`,
    deleteGoal: (id, goalId) => `/api/workstreams/${id}/goals/${goalId}`,
    // Governance
    enableGovernance: (id) => `/api/workstreams/${id}/governance/enable`,
    disableGovernance: (id) => `/api/workstreams/${id}/governance/disable`,
    // Tasks by workstream
    tasks: (id) => `/api/workstreams/${id}/tasks`
  },
  // Global Goals API
  goals: {
    list: '/api/goals',
    create: '/api/goals',
    get: (id) => `/api/goals/${id}`,
    update: (id) => `/api/goals/${id}`,
    delete: (id) => `/api/goals/${id}`,
    byWorkstream: (workstreamId) => `/api/workstreams/${workstreamId}/goals`,
    progress: (id) => `/api/goals/${id}/progress`
  },
  tasks: {
    list: '/api/tasks',
    create: '/api/tasks',
    get: (id) => `/api/tasks/${id}`,
    update: (id) => `/api/tasks/${id}`,
    delete: (id) => `/api/tasks/${id}`,
    assign: (id) => `/api/tasks/${id}/assign`,
    complete: (id) => `/api/tasks/${id}/complete`,
    addAgent: (id) => `/api/tasks/${id}/agents`,
    removeAgent: (id, agentId) => `/api/tasks/${id}/agents/${agentId}`,
    // Task views by context
    byWorkstream: (workstreamId) => `/api/workstreams/${workstreamId}/tasks`,
    bySquad: (squadId) => `/api/squads/${squadId}/tasks`,
    byUser: (userId) => `/api/users/${userId}/tasks`
  },
  chat: {
    // Unified chat API
    chat: (target, id) => `/api/chat/${target}/${id}`,
    sessions: (target, id) => `/api/chat/${target}/${id}/sessions`
  },
  governance: {
    proposals: '/api/governance/proposals',
    createProposal: '/api/governance/proposals',
    getProposal: (id) => `/api/governance/proposals/${id}`,
    deleteProposal: (id) => `/api/governance/proposals/${id}`,
    voteOnProposal: (id) => `/api/governance/proposals/${id}/vote`
  },
  public: {
    governancePoll: (id) => `/api/public/governance/polls/${id}`,
    governancePollVote: (id) => `/api/public/governance/polls/${id}/vote`
  }
}

// Create full URL for an endpoint
export const getApiUrl = (endpoint) => `${API_BASE_URL}${endpoint}`

// Generate webhook URL for a platform instance
export const generateWebhookUrl = (platform, instanceId) => {
  return `${API_BASE_URL}${API_ENDPOINTS.messaging.webhook(instanceId, platform)}`
}

// Health check endpoint
export const healthCheck = () => `${API_BASE_URL}/health`

